import React, { useState } from "react";
import { Modal, ModalBody } from "@velaro/velaro-component-lib";
import {
  AlertTriangle,
  IconClose,
  IconPlus
} from "@velaro/velaro-component-lib";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { SectionCard, PrimaryButton } from "@/components/ds";

interface MagentoConfig {
  id: number;
  siteId: number;
  displayName: string;
  storeUrl: string;
  storeName: string;
  edition: string;
  magentoVersion: string;
  enabled: boolean;
  isActive?: boolean;
  disabledAt?: string | null;
  disabledBy?: string | null;
  createdAt: string;
  accessTokenMasked: string;
}

interface Props {
  configs: MagentoConfig[];
  onDelete(id: number): void;
  onRefresh(): void;
}

export default function MagentoAccounts({
  configs,
  onDelete,
  onRefresh
}: Props) {
  const navigate = useNavigate();
  const api = useApi();
  const [deleteTarget, setDeleteTarget] = useState<MagentoConfig | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);

  async function confirmDelete() {
    if (!deleteTarget) return;
    await api.admin.delete(`Magento/Configs/${deleteTarget.id}`);
    onDelete(deleteTarget.id);
    setDeleteTarget(null);
  }

  async function saveDisplayName(config: MagentoConfig) {
    setSaving(true);
    try {
      await api.admin.put(`Magento/Configs/${config.id}`, {
        displayName: editName
      });
      onRefresh();
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled(config: MagentoConfig) {
    await api.admin.put(`Magento/Configs/${config.id}`, {
      enabled: !config.enabled
    });
    onRefresh();
  }

  async function toggleActive(config: MagentoConfig) {
    await api.admin.post(`Magento/Configs/${config.id}/toggle-active`, {});
    onRefresh();
  }

  return (
    <SectionCard
      title="Accounts"
      className="mb-4"
      action={
        <PrimaryButton
          label="Add Account"
          onClick={() => navigate("/integrations/magento/create")}
          prefixIcon={React.createElement(IconPlus)}
          className="font-medium"
        />
      }
    >
      {configs.length === 0 ? (
        <div className="py-8 text-center">
          <div className="text-zinc-500 text-sm">
            No Magento stores connected yet.
          </div>
          <div className="text-zinc-400 text-xs mt-1">
            Click "Add Account" above to connect your store.
          </div>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-zinc-500 text-xs border-b">
              <th className="pb-2 font-medium">Store</th>
              <th className="pb-2 font-medium">Edition</th>
              <th className="pb-2 font-medium">Status</th>
              <th className="pb-2 w-px whitespace-nowrap"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {configs.map((config) => (
              <tr key={config.id} className="py-2">
                <td className="py-3 pr-4">
                  {editingId === config.id ? (
                    <div className="flex gap-2">
                      <input
                        className="input text-sm w-full"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveDisplayName(config);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        autoFocus
                      />
                      <button
                        onClick={() => saveDisplayName(config)}
                        disabled={saving}
                        className="text-blue-600 text-xs px-2"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="text-zinc-400 text-xs px-1"
                      >
                        {"\u2715"}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-zinc-800">
                        {config.displayName || config.storeName || "Magento"}
                      </span>
                      <button
                        onClick={() => {
                          setEditingId(config.id);
                          setEditName(config.displayName);
                        }}
                        className="text-zinc-400 hover:text-zinc-600 text-xs opacity-0 group-hover:opacity-100"
                      >
                        {"\u270E"}
                      </button>
                    </div>
                  )}
                  <div className="text-xs text-zinc-400">
                    {config.accessTokenMasked}
                  </div>
                  {config.storeUrl && (
                    <div className="text-xs text-zinc-400 truncate">
                      {config.storeUrl}
                    </div>
                  )}
                </td>
                <td className="py-3 pr-4 text-zinc-500 text-xs">
                  {config.edition === "commerce"
                    ? "Adobe Commerce"
                    : config.edition === "open-source"
                      ? "Open Source"
                      : "\u2014"}
                </td>
                <td className="py-3 pr-4">
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => toggleEnabled(config)}
                      className={`text-xs px-2 py-0.5 rounded-full ${config.enabled ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
                    >
                      {config.enabled ? "Enabled" : "Disabled"}
                    </button>
                    <button
                      onClick={() => toggleActive(config)}
                      className={`text-xs px-2 py-0.5 rounded-full ${(config.isActive ?? true) ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}
                      title={
                        (config.isActive ?? true)
                          ? "Click to pause integration"
                          : "Click to resume integration"
                      }
                    >
                      {(config.isActive ?? true) ? "Active" : "Paused"}
                    </button>
                    {!(config.isActive ?? true) && config.disabledAt && (
                      <div className="text-xs text-amber-600 mt-0.5">
                        Paused{" "}
                        {new Date(config.disabledAt).toLocaleDateString()}
                        {config.disabledBy ? ` by ${config.disabledBy}` : ""}
                      </div>
                    )}
                  </div>
                </td>
                <td className="py-3 text-right">
                  <button
                    onClick={() => setDeleteTarget(config)}
                    className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded border border-red-200 hover:border-red-400"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {deleteTarget && (
        <Modal show={true}>
          <ModalBody>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-6 text-red-500" />
                <span className="font-semibold text-zinc-800">
                  Remove Account
                </span>
              </div>
              <button onClick={() => setDeleteTarget(null)}>
                <IconClose className="w-6 text-zinc-400" />
              </button>
            </div>
            <p className="text-sm text-zinc-600 mb-6">
              Remove{" "}
              <strong>{deleteTarget.displayName || "this account"}</strong>?
              This cannot be undone.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-5 py-2 rounded-lg border border-blue-500 text-blue-600 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </ModalBody>
        </Modal>
      )}
    </SectionCard>
  );
}
